/**
 * Nomination Review Service
 * 
 * Service layer for managing nomination review related API operations.
 * This service handles fetching, updating, and submitting nomination review data.
 */

import { apiService, type ApiResponse } from './api';

/**
 * Nomination Review data interface for API responses
 */
export interface NominationReviewResponse {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  nominationAs: string;
  employeeEmail?: string;
}

/**
 * Review Details Response interface
 */
export interface ReviewDetailsResponse {
  review_id: number;
  nomination_relationship_id: string;
  reviewer: {
    email: string;
    emp_id: string;
    name: string;
    employee_id: number;
    designation: string;
    department: string;
    manager: {
      manager_id: number;
      manager_name: string;
      manager_email: string;
    };
  };
  reviewee: {
    email: string;
    name: string;
    employee_id: number;
    designation: string;
    department: string;
    manager: {
      manager_id: number;
      manager_name: string;
      manager_email: string;
    };
  };
  cycle_id: number;
  relationship_type: string;
  status: string;
  submitted_at: string;
  competency: {
    solution_focused: number;
    adapt_to_win: number;
    courage_to_act: number;
    act_for_enterprise: number;
    own_it: number;
  };
  feedback: {
    constructive: string;
    appreciative: string;
  };
}

/**
 * Request interface for updating nomination review (new API format)
 */
export interface UpdateNominationReviewRequest {
  nomination_relationship_id: number;
  email: string;
  competency: Record<string, number>; // Dynamic competency object
  feedback: {
    constructive: string;
    appreciative: string;
  };
  is_email_required?: string;
}

/**
 * Request interface for submitting nomination review (new API format)
 */
export interface SubmitNominationReviewRequest {
  nomination_relationship_id: number;
  email: string;
  competency: Record<string, number>; // Dynamic competency object
  feedback: {
    constructive: string;
    appreciative: string;
  };
  is_email_required?: string;
}

/**
 * Nomination Review Service class
 * Handles all nomination review related API operations
 */
class NominationReviewService {
  private readonly baseEndpoint = '/api/v1/feedback/nomination_review_list';

  /**
   * Get nomination review data for a specific email
   * 
   * @param email - User email address
   * @returns Promise with nomination review data
   */
  async getNominationReviews(email: string): Promise<ApiResponse<NominationReviewResponse[]>> {
    try {
      const response = await apiService.get<NominationReviewResponse[]>(
        `${this.baseEndpoint}/?email=${encodeURIComponent(email)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching nomination reviews:', error);
      throw error;
    }
  }

  /**
   * Get specific nomination review by nomination relationship ID
   * 
   * @param email - User email address
   * @param nominationRelationshipId - Nomination relationship ID to get review for
   * @returns Promise with specific nomination review data
   */
  async getNominationReview(email: string, nominationRelationshipId: string): Promise<ApiResponse<ReviewDetailsResponse>> {
    try {
      const response = await apiService.get<ReviewDetailsResponse>(
        `/api/v1/feedback/nomination_review/?email=${encodeURIComponent(email)}&nomination_relationship_id=${nominationRelationshipId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching nomination review:', error);
      throw error;
    }
  }

  /**
   * Create nomination review (initial submission)
   * 
   * @param data - Nomination review submission data
   * @returns Promise with created nomination review data
   */
  async createNominationReview(data: SubmitNominationReviewRequest): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const response = await apiService.post<Record<string, unknown>>(
        `/api/v1/feedback/nomination_review/`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error creating nomination review:', error);
      throw error;
    }
  }

  /**
   * Update nomination review data (draft save)
   * 
   * @param data - Nomination review update data
   * @returns Promise with updated nomination review data
   */
  async updateNominationReview(data: UpdateNominationReviewRequest): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const response = await apiService.put<Record<string, unknown>>(
        `/api/v1/feedback/nomination_review/`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error updating nomination review:', error);
      throw error;
    }
  }

  /**
   * Submit nomination review (final submission)
   * 
   * @param data - Nomination review submission data
   * @returns Promise with submitted nomination review data
   */
  async submitNominationReview(data: SubmitNominationReviewRequest): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const response = await apiService.put<Record<string, unknown>>(
        `/api/v1/feedback/nomination_review/`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error submitting nomination review:', error);
      throw error;
    }
  }

  /**
   * Transform ratings array to competency object format
   * @param ratings - Array of rating objects with criterionId and rating
   * @returns Competency object with proper API keys
   */
  private transformRatingsToCompetency(
    ratings: Array<{ criterionId: string; rating: number }>
  ): Record<string, number> {
    // Map criterion IDs to API competency keys
    const competencyMapping: { [key: string]: string } = {
      '1': 'solution_focused',
      '2': 'adapt_to_win',
      '3': 'courage_to_act',
      '4': 'act_for_enterprise',
      '5': 'own_it'
    };

    const competency: Record<string, number> = {};
    ratings.forEach(rating => {
      const apiKey = competencyMapping[rating.criterionId];
      if (apiKey) {
        competency[apiKey] = rating.rating;
      }
    });
    return competency;
  }

  /**
   * Save draft - Create or update nomination review without submitting
   * 
   * @param email - User email
   * @param nominationRelationshipId - Nomination relationship ID
   * @param appreciativeFeedback - Appreciative feedback text
   * @param constructiveFeedback - Constructive feedback text
   * @param ratings - Ratings array
   * @param isEmailRequired - Whether to send copy to email ("true"/"false")
   * @returns Promise with updated data
   */
  async saveDraft(
    email: string,
    nominationRelationshipId: string,
    appreciativeFeedback: string,
    constructiveFeedback: string,
    ratings: Array<{ criterionId: string; rating: number }>,
    isEmailRequired: string = "false"
  ): Promise<ApiResponse<Record<string, unknown>>> {
    // Transform ratings to competency format
    const competency = this.transformRatingsToCompetency(ratings);
    
    const requestData = {
      nomination_relationship_id: parseInt(nominationRelationshipId),
      email,
      competency,
      feedback: {
        appreciative: appreciativeFeedback,
        constructive: constructiveFeedback
      },
      is_email_required: isEmailRequired
    };

    try {
      // First try to check if draft already exists by calling GET
      const existingDraft = await this.getNominationReview(email, nominationRelationshipId);

      if (existingDraft.status && existingDraft.data) {
        // Draft exists, use PUT to update
        console.log('Draft exists, updating with PUT');
        return this.updateNominationReview(requestData);
      } else {
        // No draft exists, use POST to create
        console.log('No draft exists, creating with POST');
        return this.createNominationReview(requestData);
      }
    } catch (err) {
      // If GET fails (404 or other error), assume no draft exists and use POST
      console.log('Draft check failed, creating new draft with POST', err);
      return this.createNominationReview(requestData);
    }
  }

  /**
   * Submit final nomination review
   * 
   * @param email - User email
   * @param nominationRelationshipId - Nomination relationship ID
   * @param appreciativeFeedback - Appreciative feedback text
   * @param constructiveFeedback - Constructive feedback text
   * @param ratings - Ratings array
   * @param sendCopyToEmail - Whether to send copy to email
   * @returns Promise with submitted data
   */
  async submitFinalReview(
    email: string,
    nominationRelationshipId: string,
    appreciativeFeedback: string,
    constructiveFeedback: string,
    ratings: Array<{ criterionId: string; rating: number }>,
    sendCopyToEmail: boolean = false
  ): Promise<ApiResponse<Record<string, unknown>>> {
    // Transform ratings to competency format
    const competency = this.transformRatingsToCompetency(ratings);
    
    const requestData = {
      nomination_relationship_id: parseInt(nominationRelationshipId),
      email,
      competency,
      feedback: {
        appreciative: appreciativeFeedback,
        constructive: constructiveFeedback
      },
      is_email_required: sendCopyToEmail ? "true" : "false"
    };

    // Always use POST for final submission
    console.log('Final submission - using POST to create/submit');
    return this.createNominationReview(requestData);
  }

  /**
   * Validate nomination review data before submission
   * 
   * @param appreciativeFeedback - Appreciative feedback text
   * @param constructiveFeedback - Constructive feedback text
   * @param ratings - Ratings array
   * @returns validation result with errors if any
   */
  validateNominationReview(
    appreciativeFeedback: string,
    constructiveFeedback: string,
    ratings: Array<{ criterionId: string; rating: number }>
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate appreciative feedback (10-10000 characters)
    const appreciativeLength = appreciativeFeedback.trim().length;
    if (appreciativeLength < 10) {
      errors.push('Appreciative feedback must be at least 10 characters');
    }
    if (appreciativeLength > 10000) {
      errors.push('Appreciative feedback must not exceed 10000 characters');
    }

    // Validate constructive feedback (10-10000 characters)
    const constructiveLength = constructiveFeedback.trim().length;
    if (constructiveLength < 10) {
      errors.push('Constructive feedback must be at least 10 characters');
    }
    if (constructiveLength > 10000) {
      errors.push('Constructive feedback must not exceed 10000 characters');
    }

    // Validate ratings
    if (ratings.length === 0) {
      errors.push('At least one rating is required');
    }

    ratings.forEach((rating, index) => {
      if (rating.rating < 1 || rating.rating > 4) {
        errors.push(`Rating ${index + 1}: Rating must be between 1 and 4`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Transform ratings from form format to API format
   * 
   * @param criteria - Criteria with ratings from form
   * @returns ratings in API format
   */
  transformRatingsToApiFormat(criteria: Array<{
    id: string;
    description: string;
    rating?: number;
  }>): Array<{ criterionId: string; rating: number }> {
    return criteria
      .filter(criterion => criterion.rating !== undefined)
      .map(criterion => ({
        criterionId: criterion.id,
        rating: criterion.rating!
      }));
  }

  /**
   * Get character count from text
   * 
   * @param text - Text to count characters for
   * @returns number of characters
   */
  getCharacterCount(text: string): number {
    return text.trim().length;
  }

  /**
   * Check if character count is valid for feedback
   * 
   * @param text - Text to validate
   * @returns validation status
   */
  isValidCharacterCount(text: string): { isValid: boolean; count: number; message?: string } {
    const count = this.getCharacterCount(text);
    
    if (count < 10) {
      return { isValid: false, count, message: 'Minimum 10 characters required' };
    }
    
    if (count > 10000) {
      return { isValid: false, count, message: 'Maximum 10000 characters allowed' };
    }
    
    return { isValid: true, count };
  }

  // Legacy method for backward compatibility
  getWordCount(text: string): number {
    return this.getCharacterCount(text);
  }

  // Legacy method for backward compatibility 
  isValidWordCount(text: string): { isValid: boolean; count: number; message?: string } {
    return this.isValidCharacterCount(text);
  }

  /**
   * Get nomination review details by nomination relationship ID
   * 
   * @param email - User email address
   * @param nominationRelationshipId - Nomination relationship ID
   * @returns Promise with review details
   */
  async getReviewDetails(email: string, nominationRelationshipId: string): Promise<ApiResponse<ReviewDetailsResponse>> {
    try {
      const response = await apiService.get<ReviewDetailsResponse>(
        `/api/v1/feedback/nomination_review/?email=${encodeURIComponent(email)}&nomination_relationship_id=${nominationRelationshipId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching review details:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const nominationReviewService = new NominationReviewService();
export default nominationReviewService;
